import {
    AssetTypeEnum,
    AssetTypeLabels,
    SiteReccePriorityEnum,
    SiteReccePriorityLabels,
    SiteRecceStatusEnum,
    SiteRecceStatusLabels,
    SiteRecceTypeEnum,
    SiteRecceTypeLabels,
} from '@api/enum';
import { ISiteReccee } from '@api/models/site-recce.model';
import FormikDatePicker from '@components/FormFields/FormikDatePicker';
import useDialog from '@hooks/use-dialog';
import { Button, Card, CircularProgress, HelpText, Label } from '@hyperflake/react-ui-library';
import { LockIcon, UnlockIcon } from '@icon/index';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage, Form, Formik } from 'formik';
import { FormikInput, FormikSelect, FormikSwitch } from 'modules/shared/features/formik';
import FormikTextarea from 'modules/shared/features/formik/components/FormikTextarea/FormikTextarea';
import useSiteRecceOperationUpdateForm from 'modules/site-recce/hooks/useSiteRecceOperationUpdateForm';
import { useAllUserList } from 'modules/users/hooks/useAllUserList';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import DeleteSiteRecceDrawer from '../DeleteSiteRecceDrawer/DeleteSiteRecceDrawer';
import FileUploadSiteRecce from '../FileUploadSiteRecce/FileUploadSiteRecce';
import AssignedSiteRecceDrawer from '../AssignedSiteRecceDrawer/AssignedSiteRecceDrawer';
import RejectSiteRecceDrawer from '../RejectSiteRecceDrawer/RejectSiteRecceDrawer';
import CompleteSiteRecceDrawer from '../CompleteSiteRecceDrawer/CompleteSiteRecceDrawer';
import SiteRecceRemarksTimeline from '../SiteRecceRemarksTimeline/SiteRecceRemarksTimeline';
import useRemarksUpdateSiteRecce from 'modules/site-recce/hooks/useRemarksUpdateSiteRecce';

interface UpdateSiteRecceFormProps {
    clientInstallationId: string;
    projectInstallationId: string;
    initialData: ISiteReccee;
    setData: (data: any) => void;
}

const OperationUpdateSiteRecceForm = (props: UpdateSiteRecceFormProps) => {
    const { clientInstallationId, projectInstallationId, initialData, setData } = props;
    const formHook = useSiteRecceOperationUpdateForm(clientInstallationId, projectInstallationId, initialData);
    const navigate = useNavigate();
    const deleteSiteRecceDailog = useDialog<ISiteReccee>();
    const assignSiteRecceDailog = useDialog<ISiteReccee>();
    const rejectSiteRecceDialog = useDialog();
    const completeSiteRecceDialog = useDialog();

    const [enableEdit, setEnableEdit] = useState<boolean>(false);
    const [searchParams] = useSearchParams();
    const { cancelRecce, completeRecce, addRemarks, removeRemark, isCancelling, isCompleting, isAdding } =
        useRemarksUpdateSiteRecce(clientInstallationId, projectInstallationId, initialData._id);

    // Get status from query param and fallback to UNASSIGNED if not present
    const routeStatus = searchParams.get('status') as SiteRecceStatusEnum;

    const { allUsers, isLoading } = useAllUserList();
    const queryClient = useQueryClient();

    const userOption = useMemo(() => {
        if (!allUsers) return [];
        return [{ label: 'Select User', value: '' }, ...allUsers.map((val) => ({ label: val.name, value: val._id }))];
    }, [allUsers]);

    const priorityOptions = useMemo(
        () =>
            Object.values(SiteReccePriorityEnum).map((priority) => ({
                label: SiteReccePriorityLabels[priority], // Use human-readable label
                value: priority,
            })),
        []
    );

    const statusOptions = useMemo(() => {
        const excludedStatuses: SiteRecceStatusEnum[] = [];

        if (routeStatus === SiteRecceStatusEnum.UNASSIGNED) {
            excludedStatuses.push(SiteRecceStatusEnum.COMPLETED);
        } else if (routeStatus === SiteRecceStatusEnum.ASSIGNED) {
            excludedStatuses.push(SiteRecceStatusEnum.UNASSIGNED);
        } else if (routeStatus === SiteRecceStatusEnum.COMPLETED) {
            excludedStatuses.push(SiteRecceStatusEnum.UNASSIGNED, SiteRecceStatusEnum.ASSIGNED);
        }

        return [
            { label: 'Select Status', value: '' },
            ...Object.values(SiteRecceStatusEnum)
                .filter((val) => !excludedStatuses.includes(val))
                .map((val) => ({
                    label: SiteRecceStatusLabels[val],
                    value: val,
                })),
        ];
    }, [routeStatus]);

    const typeOptions = useMemo(() => {
        return [
            { label: 'Select Type', value: '' },
            ...Object.values(SiteRecceTypeEnum).map((val) => ({
                label: SiteRecceTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const assetTypeOptions = useMemo(() => {
        return [
            { label: 'Select Asset', value: '' },
            ...Object.values(AssetTypeEnum).map((val) => ({
                label: AssetTypeLabels[val], // Display human-readable label
                value: val,
            })),
        ];
    }, []);

    const handleSubmit = async (values: any) => {
        // Remove properties not required for submission
        const payload = { ...values };
        delete payload.clientInstallation;
        delete payload.projectInstallation;

        // Automatically update status if applicable
        if (routeStatus === SiteRecceStatusEnum.UNASSIGNED && payload.assignedTo) {
            payload.status = SiteRecceStatusEnum.ASSIGNED;
        }

        try {
            const data = await formHook.submit(payload);
            toast.success('Recce Updated Successfully');
            setEnableEdit(false);

            await queryClient.invalidateQueries(['site-recce-counter']);
            navigate(`/all-site-recce?status=${data.status}`);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    const handleCancel = async (rejectionReason: string) => {
        try {
            const data = await cancelRecce(rejectionReason);

            toast.success('Recce Updated Successfully');
            setEnableEdit(false);

            await queryClient.invalidateQueries(['site-recce-counter']);
            navigate(`/all-site-recce?status=${data.status}`);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };

    // const handleComplete = async (values: any) => {
    //     // Remove properties not required for submission
    //     const payload = { ...values };
    //     delete payload.clientInstallation;
    //     delete payload.projectInstallation;

    //     payload.status = SiteRecceStatusEnum.COMPLETED;

    //     try {
    //         const data = await formHook.submit(payload);
    //         toast.success('Recce Updated Successfully');
    //         setEnableEdit(false);

    //         await queryClient.invalidateQueries(['site-recce-counter']);
    //         navigate(`/all-site-recce?status=${data.status}`);
    //     } catch (err: any) {
    //         toast.error(err?.data?.message || 'Something went wrong.');
    //     }
    // };
    const handleComplete = async (completionRemarks: string) => {
        try {
            const data = await completeRecce(completionRemarks);
            toast.success('Recce Completed Successfully');
            setEnableEdit(false);
            await queryClient.invalidateQueries(['site-recce-counter']);
            navigate(`/all-site-recce?status=${data.status}`);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };
    const handleAddRemarks = async (remarks: string) => {
        try {
            const data = await addRemarks(remarks);
            toast.success('Recce Completed Successfully');
            setEnableEdit(false);
            await queryClient.invalidateQueries(['site-recce-counter']);
            setData(data);
            // navigate(`/all-site-recce?status=${data.status}`);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Something went wrong.');
        }
    };
    const handleRemoveRemark = async (remarkId: string) => {
        try {
            // Call your delete API here — this is just an example:
            const data = await removeRemark(remarkId);
            toast.success('Remark removed');
            setData(data);
        } catch (err: any) {
            toast.error(err?.data?.message || 'Failed to remove remark');
        }
    };

    return (
        <>
            <Formik
                initialValues={formHook.initialValues}
                enableReinitialize
                validationSchema={formHook.schema}
                onSubmit={handleSubmit}
            >
                {({ values, isSubmitting, errors }) => {
                    return (
                        <Form>
                            <Card className="mt-4">
                                <Card.Header className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                                    {/* Left: Title */}
                                    <div className="text-base font-semibold">Site Recce Details</div>

                                    {/* Right: Actions */}
                                    <div className="flex flex-wrap gap-2 sm:gap-4 items-center sm:justify-end">
                                        {routeStatus === SiteRecceStatusEnum.UNASSIGNED && (
                                            <Button
                                                color="success"
                                                type="button"
                                                className="w-full sm:w-auto"
                                                onClick={() => assignSiteRecceDailog.show(initialData)}
                                            >
                                                Assign Recce
                                            </Button>
                                        )}

                                        {routeStatus === SiteRecceStatusEnum.ASSIGNED && (
                                            <Button
                                                color="success"
                                                type="button"
                                                className="w-full sm:w-auto"
                                                onClick={() => completeSiteRecceDialog.show()}
                                            >
                                                Complete Recce
                                            </Button>
                                        )}

                                        {(routeStatus === SiteRecceStatusEnum.UNASSIGNED ||
                                            routeStatus === SiteRecceStatusEnum.ASSIGNED) && (
                                            <Button
                                                type="button"
                                                className="w-full sm:w-auto"
                                                onClick={() => rejectSiteRecceDialog.show()}
                                            >
                                                Reject Recce
                                            </Button>
                                        )}

                                        {(routeStatus === SiteRecceStatusEnum.ASSIGNED ||
                                            routeStatus === SiteRecceStatusEnum.UNASSIGNED) && (
                                            <div className="w-full sm:w-auto">
                                                {enableEdit ? (
                                                    <div className="flex gap-4">
                                                        <Button
                                                            type="button"
                                                            variant="shaded"
                                                            className="w-full"
                                                            onClick={() => setEnableEdit(false)}
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            type="submit"
                                                            className="w-full"
                                                            disabled={isSubmitting || !enableEdit}
                                                        >
                                                            {isSubmitting ? 'Updating...' : 'Save'}
                                                        </Button>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        type="button"
                                                        variant="shaded"
                                                        className="w-full sm:w-auto"
                                                        onClick={() => setEnableEdit(true)}
                                                    >
                                                        <LockIcon
                                                            width={18}
                                                            height={22}
                                                            className="text-grayscale-600 mr-1"
                                                        />
                                                        Edit
                                                    </Button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </Card.Header>

                                <Card.Body>
                                    <fieldset disabled={!enableEdit}>
                                        {/* Client & Project Info */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                            {/* Client */}
                                            <div>
                                                <div className="text-gray-400 text-sm">Client</div>
                                                <div className="font-semibold mt-2">
                                                    {values.clientInstallation.name}
                                                </div>
                                            </div>

                                            {/* Project */}
                                            <div>
                                                <div className="text-gray-400 text-sm">Project</div>
                                                <div className="font-semibold mt-2">
                                                    {values.projectInstallation.name}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Form Fields */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
                                            {/* Type */}
                                            <div>
                                                <Label>Type</Label>
                                                <FormikSelect name="type" options={typeOptions} />
                                                <HelpText variant="error">
                                                    <ErrorMessage name="type" />
                                                </HelpText>
                                            </div>

                                            {/* Asset Type */}
                                            <div>
                                                <Label>Asset Type</Label>
                                                <FormikSelect name="assetType" options={assetTypeOptions} />
                                                <HelpText variant="error">
                                                    <ErrorMessage name="assetType" />
                                                </HelpText>
                                            </div>

                                            {/* Quantity */}
                                            <div>
                                                <Label>Quantity</Label>
                                                <FormikInput name="quantity" type="number" />
                                                <HelpText variant="error">
                                                    <ErrorMessage name="quantity" />
                                                </HelpText>
                                            </div>

                                            {/* Priority */}
                                            <div>
                                                <Label>Priority</Label>
                                                <FormikSelect name="priority" options={priorityOptions} />
                                                <HelpText variant="error">
                                                    <ErrorMessage name="priority" />
                                                </HelpText>
                                            </div>

                                            {/* Preferred Date */}
                                            <div>
                                                <Label>Preferred Date</Label>
                                                <FormikDatePicker name="preferredDate" isClearable />
                                                <HelpText variant="error">
                                                    <ErrorMessage name="preferredDate" />
                                                </HelpText>
                                            </div>

                                            {/* PO Received */}
                                            <div className="flex items-center gap-2 mt-4 md:mt-6 ">
                                                <Label htmlFor="poRecieved" className="mb-0">
                                                    PO Received
                                                </Label>
                                                <FormikSwitch name="poRecieved" color="success" />
                                            </div>

                                            {/* Assigned To & Assigned Date (only if assigned) */}
                                            {routeStatus !== SiteRecceStatusEnum.UNASSIGNED && (
                                                <>
                                                    <div>
                                                        <Label>Assigned To</Label>
                                                        <div className="flex gap-2">
                                                            <FormikSelect
                                                                name="assignedTo"
                                                                options={userOption}
                                                                disabled={routeStatus !== SiteRecceStatusEnum.ASSIGNED}
                                                            />
                                                            {isLoading && <CircularProgress size={14} />}
                                                        </div>
                                                        <HelpText variant="error">
                                                            <ErrorMessage name="assignedTo" />
                                                        </HelpText>
                                                    </div>

                                                    <div>
                                                        <Label>Assigned Date</Label>
                                                        <FormikDatePicker
                                                            name="assignedDate"
                                                            isClearable
                                                            disabled={routeStatus !== SiteRecceStatusEnum.ASSIGNED}
                                                        />
                                                        <HelpText variant="error">
                                                            <ErrorMessage name="assignedDate" />
                                                        </HelpText>
                                                    </div>
                                                </>
                                            )}
                                        </div>

                                        {/* Remarks (hidden for now but layout-ready)
        <div className="mt-6">
            <Label>Remarks</Label>
            <FormikTextarea name="remarks" rows={4} disabled={!enableEdit} />
            <HelpText variant="error"><ErrorMessage name="remarks" /></HelpText>
        </div> */}
                                    </fieldset>
                                </Card.Body>
                            </Card>

                            {/* <Button type="submit" className="mt-6" disabled={isSubmitting || !enableEdit}>
                                {isSubmitting ? 'Updating...' : 'Update Recce'}
                            </Button> */}
                        </Form>
                    );
                }}
            </Formik>

            <FileUploadSiteRecce
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                setData={setData}
                initialData={initialData}
                enableEdit={enableEdit}
            />

            {enableEdit && (
                <Card className="mt-4">
                    <Card.Header>Delete Recce</Card.Header>
                    <Card.Body>
                        <Button onClick={() => deleteSiteRecceDailog.show(initialData)}>Delete</Button>
                    </Card.Body>
                </Card>
            )}

            {/* <SiteRecceRemarksTimeline data={initialData.remarks} /> */}
            <SiteRecceRemarksTimeline
                data={initialData.remarks}
                handleAddRemarks={handleAddRemarks}
                handleRemoveRemark={handleRemoveRemark}
            />

            <DeleteSiteRecceDrawer
                clientInstallationId={clientInstallationId}
                projectInstallationId={projectInstallationId}
                onClose={deleteSiteRecceDailog.hide}
                open={deleteSiteRecceDailog.isOpen}
                siteRecce={deleteSiteRecceDailog.data}
            />
            <AssignedSiteRecceDrawer
                onClose={assignSiteRecceDailog.hide}
                open={assignSiteRecceDailog.isOpen}
                siteRecce={assignSiteRecceDailog.data}
                handleAssign={(updatedValue: ISiteReccee) => handleSubmit(updatedValue)}
            />
            <RejectSiteRecceDrawer
                onClose={rejectSiteRecceDialog.hide}
                open={rejectSiteRecceDialog.isOpen}
                onSubmit={(reason) => handleCancel(reason)}
            />
            <CompleteSiteRecceDrawer
                onClose={completeSiteRecceDialog.hide}
                open={completeSiteRecceDialog.isOpen}
                onSubmit={(remarks) => handleComplete(remarks)}
            />
        </>
    );
};

export default OperationUpdateSiteRecceForm;
